import { oc } from "@orpc/contract";
import { z } from "zod";
import { DiscountCodeKind } from "./discount-code";
import { Cents } from "./money";

/** Where the shop asked: in the checkout, in the customer account, or in the footer. */
export const NewsletterSource = z.enum(["checkout", "account", "footer"]);
export type NewsletterSource = z.infer<typeof NewsletterSource>;

/**
 * A signup from the shop. The shop shows its own newsletter checkbox, never ticked in advance, and
 * sends this only when the customer ticked it: `consent` must be `true`, anything else is refused.
 */
export const NewsletterSubscription = z.object({
	/** Stored trimmed and in lower case: one address is one signup however it is typed. */
	email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
	source: NewsletterSource,
	consent: z.literal(true),
	/**
	 * What the Turnstile widget put in the form (`cf-turnstile-response`). Required for a guest; a
	 * signed-in customer (the customer session cookie) needs none, as at checkout and contact, and may
	 * send it empty.
	 */
	turnstileToken: z.string().optional(),
});

/**
 * The signed-in customer's own signup, as the account's switch shows and sets it: whether their
 * account's address is signed up now (an unsubscribed signup is not).
 */
export const AccountNewsletter = z.object({ subscribed: z.boolean() });
export type AccountNewsletter = z.infer<typeof AccountNewsletter>;

/** The token from an unsubscribe link. */
export const NewsletterUnsubscribe = z.object({ token: z.string().min(1).max(1024) });

/** How many addresses are signed up now; unsubscribed ones are not counted. */
export const NewsletterCount = z.object({ active: z.number().int() });
export type NewsletterCount = z.infer<typeof NewsletterCount>;

/**
 * The welcome discount (#108): every new signup gets one single-use discount code of this kind,
 * valid `validityDays` days from the signup, mailed to the address. An address gets it once, ever:
 * signing up again, or again after an unsubscribe, makes no second code. Amounts are integer
 * cents.
 */
export const WelcomeDiscount = z.object({
	/** Off: new signups get no welcome code and no welcome mail. */
	enabled: z.boolean(),
	kind: DiscountCodeKind,
	validityDays: z.number().int().min(1).max(365),
	/** The goods total before discount must reach this; `null`: no minimum. */
	minimumOrderAmount: Cents.min(0).max(1_000_000).nullable(),
});
export type WelcomeDiscount = z.infer<typeof WelcomeDiscount>;

/** What applies until Richard saves his own settings: 10% off, 30 days, from €25 of products. */
export const DEFAULT_WELCOME_DISCOUNT: WelcomeDiscount = {
	enabled: true,
	kind: { type: "percentage", percent: 10 },
	validityDays: 30,
	minimumOrderAmount: 2500,
};

/**
 * The welcome discount as the shop may tell anyone (#195): whether a first signup gets a welcome
 * code now, and if so which discount, so the signup's confirmation only promises one when it comes.
 */
export const ShopWelcomeOffer = z.discriminatedUnion("enabled", [
	z.object({ enabled: z.literal(false) }),
	WelcomeDiscount.extend({ enabled: z.literal(true) }),
]);
export type ShopWelcomeOffer = z.infer<typeof ShopWelcomeOffer>;

/** The settings with their `version` (0 while the defaults apply), for the next save. */
export const NewsletterSettings = z.object({ welcome: WelcomeDiscount, version: z.number().int().min(0) });
export type NewsletterSettings = z.infer<typeof NewsletterSettings>;

/** A save: the new settings and the `version` they were loaded at; a stale save is refused (409). */
export const NewsletterSettingsUpdate = NewsletterSettings;
export type NewsletterSettingsUpdate = z.infer<typeof NewsletterSettingsUpdate>;

/**
 * The newsletter: the shop's public signup and unsubscribe, which both answer 204 and say nothing
 * about whether the address was signed up before; and the admin's count and export,
 * which need a staff session.
 */
export const NewsletterContract = {
	/** The number of active signups, as the export would list them. */
	count: oc.route({ method: "GET", path: "/admin/newsletter/count" }).output(NewsletterCount),
	/**
	 * The active signups as a CSV download (`email,subscribed_at,source`, oldest first); unsubscribed
	 * addresses are left out. The client receives it as a `File`.
	 */
	export: oc.route({ method: "GET", path: "/admin/newsletter/export" }).output(z.file()),
	/** The welcome discount's settings (admin). */
	settings: oc.route({ method: "GET", path: "/admin/newsletter/settings" }).output(NewsletterSettings),
	/** Saves them; 409 `NewsletterSettingsChanged` when someone saved meanwhile. */
	updateSettings: oc
		.route({ method: "PUT", path: "/admin/newsletter/settings" })
		.input(NewsletterSettingsUpdate)
		.output(NewsletterSettings),
	/**
	 * Signs the address up; signing up again changes nothing, and reactivates an unsubscribed one.
	 * An address that never had a welcome code gets one by mail (#108).
	 */
	subscribe: oc
		.route({ method: "POST", path: "/shop/newsletter/subscribe", successStatus: 204 })
		.input(NewsletterSubscription),
	/** Public: whether a first signup gets a welcome code now, and which discount (cached 5 minutes). */
	welcome: oc.route({ method: "GET", path: "/shop/newsletter/welcome" }).output(ShopWelcomeOffer),
	/**
	 * Whether the signed-in customer's account address is signed up (the switch in "Gegevens").
	 * Errors: `CustomerNotSignedIn` (401).
	 */
	account: oc.route({ method: "GET", path: "/shop/account/newsletter" }).output(AccountNewsletter),
	/**
	 * The switch: `true` signs the account's address up as `subscribe` does (source `account`, no
	 * Turnstile, the welcome code for a first signup); `false` unsubscribes it, as its link would.
	 * Answers where it stands now. Errors: `CustomerNotSignedIn` (401), `RateLimited` (429).
	 */
	setAccount: oc
		.route({ method: "PUT", path: "/shop/account/newsletter" })
		.input(AccountNewsletter)
		.output(AccountNewsletter),
	/** Unsubscribes the address the link was made for; a second time changes nothing. */
	unsubscribe: oc
		.route({ method: "POST", path: "/shop/newsletter/unsubscribe", successStatus: 204 })
		.input(NewsletterUnsubscribe),
};
